import { computed, inject, Injectable, signal } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Settings } from 'luxon'
import { CookieService } from 'ngx-cookie-service'
import { EMPTY, catchError, switchMap } from 'rxjs'
import { ENVIRONMENT } from '../environment.token'
import { USER_LANGUAGE_API } from './language.api'

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private translate = inject(TranslateService)
  private cookieService = inject(CookieService)
  private userLanguageApi = inject(USER_LANGUAGE_API, { optional: true })
  private loggedIn = false
  private userLangLoaded = false

  environment = inject(ENVIRONMENT)

  private readonly lang = signal('')
  private readonly cookieLang = signal(this.cookieService.get(this.environment.langCookieName))

  readonly userLang = computed(() => this.cookieLang() || this.lang())
  readonly availableLanguages = this.environment.availableLanguages.map((lang) => lang.code)

  constructor() {
    this.lang.set(this.cookieService.get(this.environment.langCookieName) || (this.translate.getBrowserLang() ?? ''))
  }

  initLang(langParam: string) {
    // a language loaded from the user profile wins over a later ?lang= or china default
    if (this.userLangLoaded) return

    if (this.environment.isChina) {
      this.lang.set('zh')
    }

    if (langParam && !this.cookieLang()) {
      this.lang.set(langParam)
    }

    this.translate.addLangs(this.availableLanguages)
    if (this.lang() && this.availableLanguages.includes(this.lang())) {
      this.translate.setFallbackLang('en')
      this.translate.use(this.lang())
      Settings.defaultLocale = this.lang()
    } else {
      this.translate.setFallbackLang('en')
      this.translate.use('en')
      Settings.defaultLocale = 'en'
    }
  }

  updateLang(value: string) {
    this.applyLang(value)
    if (this.loggedIn) this.userLanguageApi?.setUserLang(value).pipe(catchError(() => EMPTY)).subscribe()
  }

  // the language stored at the customer is the primary source, cookie, ?lang= and browser language are the fallback
  syncUserLang(loggedIn: boolean) {
    if (!loggedIn || !this.userLanguageApi) {
      this.loggedIn = false
      return
    }
    if (this.loggedIn) return
    this.loggedIn = true

    const api = this.userLanguageApi
    api.getUserLang().pipe(
      switchMap((lang) => {
        if (!lang) return api.setUserLang(this.availableLanguages.includes(this.lang()) ? this.lang() : 'en')
        if (this.availableLanguages.includes(lang)) {
          this.userLangLoaded = true
          if (lang !== this.translate.getCurrentLang()) this.applyLang(lang)
        }
        return EMPTY
      }),
      catchError(() => EMPTY)
    ).subscribe()
  }

  private applyLang(value: string) {
    this.lang.set(value)
    this.translate.use(value)
    Settings.defaultLocale = value == 'zh' ? 'zh_cn' : value
    this.cookieService.set(this.environment.langCookieName, value, {
      path: '/',
      sameSite: 'Lax',
      secure: true,
      expires: 365
    })
    this.cookieLang.set(value)
  }
}
